import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import Switch from '@material-ui/core/Switch';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';

import { DefaultInput } from 'shared/components/text-input-with-label';
import useCart from 'hooks/use-cart';
import useUI from 'hooks/use-ui';
import { useLoyalty } from 'components/widgets/loyalty-tiers/use-loyalty';
import { DposApplyRewardsCheckout } from 'components/loyalty/rewards/dpos-apply-rewards-checkout';
import { RedemptionType, Mode } from 'components/loyalty/rewards/dpos-utils';
import type { RewardV2 } from 'src/gql/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const LoyaltyWidget = observer(
  (): JSX.Element => {
    const flags = useFlags();
    const useCashRedemptionCheckoutRollout = flags['growth.ecomm.cash-redemption-checkout-fix.rollout'] ?? false;
    const useLoyaltyDiscountValueFix = flags['growth.ecomm.loyalty-discount-value-fix.rollout'] ?? false;
    const isCheckoutWidgetOnKioskEnabled = flags['pos.ecomm.checkout-widget-on-kiosk.rollout'] ?? false;
    const UI = useUI();
    const Cart = useCart();
    const [pointsApplied, setPointsApplied] = useState<string>('0');
    const [applyMyRewards, setApplyMyRewards] = useState<boolean>(false);
    const { dispensary, loyaltyPointsApplied } = Cart;
    const {
      loyaltyConfig,
      rewardsArray,
      posCustomerId,
      showDiscountRewardsOnly,
      soonestExpiringBalance,
      soonestExpirationDate,
      balanceDaysToExpiration,
      formatExpirationDate,
      formatValueForDisplay,
      formatValueForDaysForExpire,
      isDiscountRedemption,
    } = useLoyalty(undefined, undefined, undefined, { allowOnKiosk: isCheckoutWidgetOnKioskEnabled });
    const redemptionType = loyaltyConfig?.redemptionType;
    const mode = loyaltyConfig?.mode;

    const handleApplyReward = (costV2: number, copy: string, id: string): void => {
      // Find the reward to get its value
      const reward = rewardsArray.find((r: RewardV2) => r.id === id);
      const rewardValue = reward?.value;
      const rawValue = Number(rewardValue) || 0;
      // Percentage rewards store the percent (e.g. 10 for 10%) in `value`, not a dollar
      // amount - convert to a dollar figure the same way order receipts do (operator '$' vs '%').
      // Gated behind growth.ecomm.loyalty-discount-value-fix.rollout (ECOMM-11358) - falls back
      // to the pre-fix costV2 behavior when the flag is off.
      const fixedDiscountValue = reward?.operator === '%' ? rawValue * 0.01 * Cart.subtotal : rawValue;
      const discountValue = useLoyaltyDiscountValueFix ? fixedDiscountValue : costV2;

      const appliedReward = {
        id: reward?.id,
        discountId: reward?.discountId,
        copy,
        value: rewardValue,
        operator: reward?.operator,
        available: reward?.available,
        brand: reward?.brand,
      };

      Cart.applyReward(appliedReward);
      Cart.posCustomerId = posCustomerId ?? '';

      // Also update loyaltyPointsApplied for backwards compatibility. This must be the
      // reward's dollar value, not costV2 (the points cost of the reward) - otherwise
      // discount rewards get applied as if they were cash redemptions of that many points.
      // (See useLoyaltyDiscountValueFix above for the flag gating this correction.)
      Cart.loyaltyPointsApplied = discountValue;
      Cart.loyaltyAppliedAsDiscount = discountValue;
      setPointsApplied(discountValue.toString());
      setApplyMyRewards(true);
    };

    const handleUndoReward = (): void => {
      Cart.appliedRewards = [];

      Cart.loyaltyPointsApplied = 0;
      setPointsApplied('0');
    };

    useEffect(() => {
      const pointsAsNumber = Number(pointsApplied);
      if (Number.isNaN(pointsAsNumber)) {
        return;
      }
      if (pointsAsNumber !== loyaltyPointsApplied) {
        setPointsApplied((loyaltyPointsApplied ?? 0).toString());
      }
    }, [pointsApplied, loyaltyPointsApplied]);

    function handleCashRedemption(event: React.ChangeEvent<HTMLInputElement>, resetValue: boolean): void {
      let { value } = event.target;
      value = resetValue ? '0' : value;

      let pointsAsNumber = Number(value);
      const rewardBalanceNumber = Number(Cart.rewardsBalance ?? 0);
      if (Number.isNaN(pointsAsNumber) || Number.isNaN(rewardBalanceNumber)) {
        setPointsApplied(value);
        return;
      }
      if (pointsAsNumber > rewardBalanceNumber) {
        pointsAsNumber = rewardBalanceNumber;
      }
      if (pointsAsNumber > Cart.subtotal - 1) {
        pointsAsNumber = Cart.subtotal - 1;
      }
      Cart.loyaltyPointsApplied = pointsAsNumber;

      if (pointsAsNumber !== Number(value)) {
        setPointsApplied(pointsAsNumber.toString());
      } else {
        setPointsApplied(value);
      }
    }

    const showSoonestExpiry = soonestExpiringBalance != null && !!soonestExpirationDate;
    const rewardsBalanceNumber = Number(Cart.rewardsBalance ?? 0);
    let rewardsBalanceDisplay = '';
    if (redemptionType === RedemptionType.cash) {
      rewardsBalanceDisplay = `$${rewardsBalanceNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (redemptionType === RedemptionType.discount) {
      rewardsBalanceDisplay = `${rewardsBalanceNumber.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} pts`;
    }

    const ApplyRewardsBlock = (
      <>
        <FormGroup>
          <StyledFormControlLabel
            control={
              <StyledSwitch
                onChange={(event) => {
                  setApplyMyRewards(event.target.checked);
                  if (!event.target.checked) {
                    if (redemptionType === RedemptionType.cash) {
                      setPointsApplied('0');
                      handleCashRedemption(event, true);
                    } else {
                      handleUndoReward();
                    }
                  } else if (event.target.checked) {
                    if (
                      !useCashRedemptionCheckoutRollout ||
                      (redemptionType && redemptionType.toLowerCase() === RedemptionType.cash.toLowerCase())
                    ) {
                      const maxPoints = Math.min(Cart.subtotal - 1, Number(Cart.rewardsBalance));
                      Cart.loyaltyPointsApplied = maxPoints;
                      setPointsApplied(maxPoints.toString());
                    }
                  }
                }}
              />
            }
            label='Apply my rewards'
          />
        </FormGroup>
        {applyMyRewards &&
          redemptionType === RedemptionType.cash &&
          (mode === Mode.cash ? (
            <InStoreText className='fine-print'>
              <b>Redeemable in-store only.</b> Cannot be used online.
            </InStoreText>
          ) : (
            <>
              <PointsTextInput
                onChange={handleCashRedemption}
                value={pointsApplied}
                fullWidth
                type='number'
                inputMode='decimal'
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                }}
              />
              <NormalText className='fine-print'>Rewards applied cannot be greater than subtotal</NormalText>
            </>
          ))}
        {applyMyRewards && redemptionType === RedemptionType.discount && (
          <DposApplyRewardsCheckout
            rewards={rewardsArray}
            onApplyReward={handleApplyReward}
            onUndoReward={handleUndoReward}
            rewardsBalanceNumber={rewardsBalanceNumber}
            showDiscountRewardsOnly={showDiscountRewardsOnly}
          />
        )}
      </>
    );

    // View-only mode ("View rewards only (redeem in-store)" in BOH). Discount redemption still
    // shows the customer's actual eligible rewards via DposApplyRewardsCheckout — it renders the
    // in-store-only header itself and disables each reward card — rather than a bare text block,
    // so customers can see what they have even though they must redeem in-store.
    const ViewOnlyRewardsBlock = (
      <>
        {redemptionType === RedemptionType.cash && (
          <InStoreText className='fine-print'>
            <b>Redeemable in-store only.</b> Cannot be used online.
          </InStoreText>
        )}
        {redemptionType === RedemptionType.discount && (
          <DposApplyRewardsCheckout
            rewards={rewardsArray}
            onApplyReward={handleApplyReward}
            onUndoReward={handleUndoReward}
            rewardsBalanceNumber={rewardsBalanceNumber}
            showDiscountRewardsOnly={showDiscountRewardsOnly}
          />
        )}
      </>
    );
    return (
      <Wrapper isKiosk={UI.isKiosk}>
        <Header>
          <ImageContainer>
            <img src={dispensary.logoImage} alt='' />
          </ImageContainer>
          <Amount>{rewardsBalanceDisplay}</Amount>
          <NormalText>Available</NormalText>
          {showSoonestExpiry && (
            <ExpirationText>
              {formatValueForDisplay(
                soonestExpiringBalance,
                isDiscountRedemption ? 'pts' : 'cash',
                isDiscountRedemption
              )}{' '}
              expire {formatExpirationDate(soonestExpirationDate)}
            </ExpirationText>
          )}
          {!showSoonestExpiry && rewardsBalanceNumber > 0 && (
            <ExpirationText>{formatValueForDaysForExpire(balanceDaysToExpiration ?? 0)}</ExpirationText>
          )}
        </Header>
        {showDiscountRewardsOnly ? <Content>{ViewOnlyRewardsBlock}</Content> : <Content>{ApplyRewardsBlock}</Content>}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div<{ isKiosk?: boolean }>`
  border-radius: var(--dct--customized-radius-sm, ${({ theme }) => theme.customized.radius.sm});
  overflow: hidden;
  ${({ isKiosk }) => !isKiosk && 'max-width: 390px;'}
  display: flex;
  flex-direction: column;
  border: 1px solid #d8dee3;

  .MuiTypography-body1 {
    font-family: var(--dct--customized-fonts-secondary, ${({ theme }) => theme.customized.fonts.secondary});
  }
`;
const Header = styled.div`
  background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme.customized.colors.buttonsLinks});
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 1;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: left;
  padding: 16px 20px;

  &::before {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    content: '';
    height: 316px;
    width: 316px;
    left: -90px;
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

const Amount = styled.span`
  font-family: var(--dct--customized-fonts-primary, ${({ theme }) => theme.customized.fonts.primary});
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-right: 4px;
`;

const NormalText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;

  &.fine-print {
    color: ${({ theme }) => theme.colors.grey[45]};
  }
`;

const ExpirationText = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
  margin-left: auto;
  white-space: nowrap;
`;

const InStoreText = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;

  &.fine-print {
    color: ${({ theme }) => theme.colors.grey[45]};
  }
`;

const ImageContainer = styled.div`
  display: block;
  height: 40px;
  width: 40px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
  }
`;
const Content = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledSwitch = styled(Switch)`
  &.MuiSwitch-root {
    width: 40px;
    height: 24px;
    border-radius: 14px;
    margin-left: 11px;
    margin-right: 12px;
    padding: 0px;

    .MuiSwitch-switchBase.Mui-checked {
      transform: translateX(16px);
    }

    .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.grey[75]};
      opacity: 1;
    }
    .Mui-checked + .MuiSwitch-track {
        background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) =>
          theme.customized.colors.buttonsLinks});
        opacity: 1;
      }
    }

    .MuiSwitch-colorSecondary.Mui-checked:hover {
      background: none;
    }
    .MuiButtonBase-root {
      padding: 2px;
    }
    .MuiSwitch-thumb {
      background-color: #fff;
      height: 20px;
      width: 20px;
      box-shadow: none;
    }
    .Mui-checked {
      .MuiSwitch-thumb {
        background-color: #fff;
        background-image: url('/icons/check-small.svg');
        background-size: 9px 7px;
        background-repeat: no-repeat;
        background-position: center;
      }
    }
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-bottom: 12px;
  .MuiTypography-root {
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    letter-spacing: 0%;
  }
`;

const PointsTextInput = styled(DefaultInput)`
  margin: 0px;
  width: 246px;
  height: 48px;
  padding: 1px;

  .MuiFilledInput-adornedStart {
    padding-left: 22px;
  }

  .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel),
  .MuiInputAdornment-root {
    margin: 0px;

    .MuiTypography-body1 {
      font-family: var(--dct--customized-fonts-primary, ${({ theme }) => theme.customized.fonts.primary});
      font-size: 15px;
    }
  }

  font-weight: 400;
  letter-spacing: 0%;

  .MuiInputBase-input {
    padding: 12px;
    padding-left: 0px;
    font-size: 15px;
  }
  .Mui-focused {
    box-shadow: 0px 0px 4px 0px #4c94d780;
  }

  .Mui-focused > input {
    box-shadow: none !important;
    border: none !important;
  }
`;
